import { useEffect, useMemo } from 'react';
import Footer from '../home/components/Footer';
import SEOHead from '../../components/feature/SEOHead';

const SITE_URL = (import.meta.env.VITE_SITE_URL as string) || 'https://example.com';

export default function DailySupport() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const schemas = useMemo(() => {
    const service = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: '日常生活支援サービス',
      description:
        'まごころサービスの日常生活支援は、ご利用者様が快適で安心した毎日を過ごせるよう、きめ細やかなお手伝いをいたします。買い物代行、掃除・片付け、洗濯・衣類の整理、通院・外出支援、生活相談・安否確認などのサービスを提供しています。',
      provider: {
        '@type': 'LocalBusiness',
        name: 'まごころサービス',
        url: SITE_URL,
        telephone: '090-7289-8910',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '金井ヶ丘4-8',
          addressLocality: '町田市',
          addressRegion: '東京都',
          postalCode: '194-0036',
          addressCountry: 'JP',
        },
      },
      serviceType: '介護保険外サービス',
      offers: {
        '@type': 'Offer',
        price: '3850',
        priceCurrency: 'JPY',
        unitText: '時間',
        description: '1時間あたり ¥3,850（税込）',
      },
      areaServed: {
        '@type': 'AdministrativeArea',
        name: '東京都町田市周辺',
      },
      inLanguage: 'ja',
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: '日常生活支援サービス一覧',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '買い物代行' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '掃除・片付け' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '洗濯・衣類の整理' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '通院・外出支援' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '生活相談・安否確認' } },
        ],
      },
    };

    const webPage = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: '日常生活支援サービス | まごころサービス',
      description:
        'まごころサービスの日常生活支援サービスでは、買い物代行・掃除・洗濯・通院付き添い・安否確認など幅広くサポートします。1時間¥3,850（税込）。東京都町田市周辺で地域密着型の介護保険外サービスを提供しています。',
      url: `${SITE_URL}/daily-support`,
      inLanguage: 'ja',
      isPartOf: {
        '@type': 'WebSite',
        name: 'まごころサービス',
        url: SITE_URL,
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'トップページ',
            item: SITE_URL,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: '日常生活支援サービス',
            item: `${SITE_URL}/daily-support`,
          },
        ],
      },
    };

    return [service, webPage];
  }, []);

  const services = [
    {
      icon: 'ri-shopping-cart-2-line',
      title: '買い物代行',
      description: '食材や日用品の買い物をお手伝いします。重い荷物の運搬や商品選びの相談も承ります。'
    },
    {
      icon: 'ri-brush-line',
      title: '掃除・片付け',
      description: '居室の掃除や整理整頓を行い、きれいで心地よい環境づくりを支援します。'
    },
    {
      icon: 'ri-shirt-line',
      title: '洗濯・衣類の整理',
      description: '洗濯や干す作業、衣類の整理収納もお任せください。'
    },
    {
      icon: 'ri-hospital-line',
      title: '通院・外出支援',
      description: '病院や役所への付き添い、買い物や散歩の同行など、安全に外出できるようサポートします。'
    },
    {
      icon: 'ri-chat-heart-line',
      title: '生活相談・安否確認',
      description: '日常の不安や悩みをお聞きし、必要に応じて適切なサポートへの橋渡しも行います。'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50/50 to-white">
      <SEOHead
        title="日常生活支援サービス | まごころサービス | 介護保険外の生活支援"
        description="まごころサービスの日常生活支援サービスでは、買い物代行・掃除・洗濯・通院付き添い・安否確認など幅広くサポートします。1時間¥3,850（税込）。東京都町田市周辺で地域密着型の介護保険外サービスを提供しています。"
        canonicalPath="/daily-support"
        schemas={schemas}
      />
      {/* ヘッダー */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a 
            href="/"
            className="flex items-center gap-2 text-gray-800 hover:text-pink-500 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl"></i>
            <span className="font-semibold" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>トップページに戻る</span>
          </a>
          <a 
            href="/#contact"
            className="bg-pink-500 text-white px-6 py-2 rounded-full text-sm font-semibold hover:bg-pink-600 transition-all whitespace-nowrap cursor-pointer"
          >
            今すぐ相談する
          </a>
        </div>
      </header>

      {/* ヒーローセクション */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-pink-100 rounded-full mb-6">
              <i className="ri-home-heart-line text-5xl text-pink-500"></i>
            </div>
            <h1 className="text-5xl md:text-6xl font-black text-gray-800 mb-6" style={{ fontFamily: "'Zen Maru Gothic', sans-serif" }}>
              日常生活支援サービス
            </h1>
            <p className="text-lg text-gray-600 font-light max-w-3xl mx-auto leading-relaxed" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              まごころサービスの日常生活支援は、ご利用者様が快適で安心した毎日を過ごせるよう、きめ細やかなお手伝いをいたします。
            </p>
          </div>

          {/* 料金表示 */}
          <div className="bg-gradient-to-r from-pink-500 to-orange-400 rounded-3xl p-8 text-center text-white mb-16 shadow-xl">
            <p className="text-lg font-light mb-2" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>ご利用料金</p>
            <p className="text-5xl font-black mb-2" style={{ fontFamily: "'Zen Maru Gothic', sans-serif" }}>¥3,850</p>
            <p className="text-xl font-light" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>1時間あたり</p>
          </div>
        </div>
      </section>

      {/* サービス詳細 */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-black text-gray-800 mb-12 text-center" style={{ fontFamily: "'Zen Maru Gothic', sans-serif" }}>
            サービスの具体例
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-pink-50 to-orange-50 rounded-3xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-white rounded-full mb-6 shadow-md">
                  <i className={`${service.icon} text-4xl text-pink-500`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Zen Maru Gothic', sans-serif" }}>
                  {service.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed font-light" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* サービスの特徴 */}
      <section className="py-16 px-6 bg-gradient-to-b from-white to-pink-50/30">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-black text-gray-800 mb-12 text-center" style={{ fontFamily: "'Zen Maru Gothic', sans-serif" }}>
            まごころサービスの特徴
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-pink-100 rounded-full mx-auto mb-6">
                <i className="ri-user-heart-line text-5xl text-pink-500"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Zen Maru Gothic', sans-serif" }}>
                一人ひとりに寄り添う
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed font-light" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                お客様のペースや生活スタイルに合わせた、きめ細やかなサポートを心がけています。
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-orange-100 rounded-full mx-auto mb-6">
                <i className="ri-shield-check-line text-5xl text-orange-500"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Zen Maru Gothic', sans-serif" }}>
                安心・安全なサービス
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed font-light" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                経験豊富なスタッフが、丁寧で安全なサービスを提供いたします。
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-purple-100 rounded-full mx-auto mb-6">
                <i className="ri-time-line text-5xl text-purple-500"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Zen Maru Gothic', sans-serif" }}>
                柔軟な対応
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed font-light" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                急なご依頼やスケジュール変更にも、できる限り柔軟に対応いたします。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTAセクション */}
      <section className="py-20 px-6 bg-gradient-to-r from-pink-500 to-orange-400">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6" style={{ fontFamily: "'Zen Maru Gothic', sans-serif" }}>
            まずはお気軽にご相談ください
          </h2>
          <p className="text-lg text-white/90 mb-10 font-light" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
            お客様のご要望やお悩みをお聞かせください。<br />
            最適なサポートプランをご提案いたします。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/#contact"
              className="bg-white text-pink-500 px-10 py-4 rounded-full text-lg font-bold hover:bg-pink-50 transition-all duration-300 hover:shadow-xl whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
            >
              <i className="ri-phone-line text-xl"></i>
              今すぐ相談する
            </a>
            <a 
              href="/#contact"
              className="bg-pink-900 text-white px-10 py-4 rounded-full text-lg font-bold hover:bg-pink-800 transition-all duration-300 hover:shadow-xl whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
            >
              <i className="ri-mail-line text-xl"></i>
              お問い合わせフォーム
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
